import { add, compareAsc, compareDesc, eachDayOfInterval, sub } from "date-fns";
import { useMemo } from "react";

import { parseStringToDate } from "~/modules/app/util/dateHelpers";

import { calculatePlannerVisitPossibleDaysStatus } from "../calculatePlannerVisitPossibleDaysStatus";
import { VisitStatusTimelineFragment } from "../fragment.generated";

type UseTimelineRangeParams = {
    possibleDays: VisitStatusTimelineFragment["possibleDays"];
};

// we add a couple of days before and after the possible day ranges for padding
// this results in the "not yet plannable" and "out of protocol days"
// its cute, its fun.
const TIMELINE_PADDING_DAYS = 10;

export function useTimelineRange({ possibleDays }: UseTimelineRangeParams) {
    return useMemo(() => {
        if (possibleDays.length === 0) return null;

        const firstStartDate = parseStringToDate(
            [...possibleDays].sort((a, b) => compareAsc(parseStringToDate(a.start), parseStringToDate(b.start)))[0]?.start,
        );

        const latestEndDate = parseStringToDate(
            [...possibleDays].sort((a, b) => compareDesc(parseStringToDate(a.end), parseStringToDate(b.end)))[0]?.end,
        );

        const firstDateInRange = sub(firstStartDate, { days: TIMELINE_PADDING_DAYS });
        const lastDateInRange = add(latestEndDate, { days: TIMELINE_PADDING_DAYS });

        const daysInRange = eachDayOfInterval({
            start: firstDateInRange,
            end: lastDateInRange,
        });

        const firstStartDayIndex = TIMELINE_PADDING_DAYS;
        const latestEndDayIndex = daysInRange.length - TIMELINE_PADDING_DAYS;

        const dayData = daysInRange.map((day, index) => ({
            day,
            index,
            status: calculatePlannerVisitPossibleDaysStatus(possibleDays, day),
        }));

        return {
            firstStartDate,
            latestEndDate,
            firstDateInRange,
            lastDateInRange,
            firstStartDayIndex,
            latestEndDayIndex,
            dayData,
        };
    }, [possibleDays]);
}
